import React from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import { useMarkdownContent } from "../hooks/use-markdown-content";
import LoadingAnimation from "src/components/Animations/LoadingAnimation";

interface DocsContentProps {
  markdownUrl: string;
  title?: string;
}

/**
 * Component that renders markdown content with consistent styling.
 * Handles loading and error states.
 */
function DocsContent({ markdownUrl, title }: DocsContentProps) {
  const { t } = useTranslation("common");
  const { content, isLoading, error } = useMarkdownContent(markdownUrl);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoadingAnimation containerClassName="h-screen centered" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-10 text-danger">
            {t("errorLoadingContent")}
          </h2>
          <p className="text-muted">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-20 mx-auto">
      <div className="prose prose-invert max-w-none">
        <div className="markdown-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSlug]}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

export default React.memo(DocsContent);
